// Frontend mirror of the backend FDA ingredient-statement generator
// (/backend/recipes/shared/ingredientStatement.ts). The caller supplies each
// line's `weight` (a comparable magnitude — see predominance in ./units).

import { normalizeColorNames } from './colorNames'

export interface StatementLine {
  name: string
  /** Free-text sub-ingredient list, already in order (e.g. "Cream, Salt"). */
  sub_ingredients?: string | null
  /** Comparable magnitude for ordering (larger = more predominant). */
  weight?: number | null
}

/** Most characters the label has room for in its ingredient list. */
export const LABEL_INGREDIENTS_MAX = 800

/**
 * At or below this share of the recipe's weight an ingredient can go after
 * "CONTAINS 2% OR LESS OF:" in any order (21 CFR 101.4(a)(2)).
 */
const MINOR_SHARE = 0.02
const MINOR_HEADING = 'CONTAINS 2% OR LESS OF:'

export interface IngredientStatement {
  /** What goes on the label: the full list, or the short form when the full one is too long. */
  statement: string
  /** Every ingredient with its sub-ingredients in parentheses, in weight order. */
  full: string
  /** True when `statement` is the short form. */
  shortened: boolean
}

/**
 * Returns the ordered, FDA-style ingredient statement, e.g.
 * "Popcorn, Brown Sugar (Sugar, Molasses, Sea Salt), Butter (Cream, Salt), Salt".
 * Over LABEL_INGREDIENTS_MAX characters it returns the short form (see
 * buildIngredientStatement).
 */
export function generateIngredientStatement(lines: StatementLine[]): string {
  return buildIngredientStatement(lines).statement
}

/**
 * Builds the full statement and, when it's longer than the label allows, a
 * short form that keeps the same FDA meaning:
 *
 * - Ingredients over 2% of the recipe weight are listed as usual, in order.
 * - The rest go after "CONTAINS 2% OR LESS OF:". Their sub-ingredients are
 *   listed on their own instead of under the parent's name (101.4(b)(2)(ii)),
 *   so an ingredient shared by several colors or flavors is listed once:
 *   five "RED/BLUE/... COLOR (GLYCERIN, WATER, ...)" become one GLYCERIN,
 *   one XANTHAN GUM, and each FD&C color once.
 * - "NATURAL FLAVORS", "ARTIFICIAL FLAVORS" and "NATURAL AND ARTIFICIAL
 *   FLAVORS" merge into one term covering what's there (101.22(h)).
 * - A sub-ingredient that's already a main ingredient (WATER) isn't repeated.
 *
 * Each split-out sub-ingredient weighs at most its parents' combined weight;
 * if that could top 2%, the short form isn't used.
 */
export function buildIngredientStatement(
  lines: StatementLine[],
  maxLength: number = LABEL_INGREDIENTS_MAX,
): IngredientStatement {
  const sorted = lines
    .map((l, i) => ({ line: l, i }))
    .filter(({ line }) => (line.name ?? '').trim() !== '')
    .sort((a, b) => {
      const wa = a.line.weight ?? -Infinity
      const wb = b.line.weight ?? -Infinity
      if (wb !== wa) return wb - wa
      return a.i - b.i // stable: keep input order on ties
    })
    .map(({ line }) => line)

  const full = sorted.map(fullEntry).join(', ')
  if (full.length <= maxLength) return { statement: full, full, shortened: false }
  const short = shortStatement(sorted)
  return short != null && short.length < full.length
    ? { statement: short, full, shortened: true }
    : { statement: full, full, shortened: false }
}

function fullEntry(line: StatementLine): string {
  const name = line.name.trim()
  const sub = (line.sub_ingredients ?? '').trim()
  return normalizeColorNames(sub ? `${name} (${sub})` : name).toUpperCase()
}

function shortStatement(sorted: StatementLine[]): string | null {
  const total = sorted.reduce((s, l) => s + (l.weight != null && l.weight > 0 ? l.weight : 0), 0)
  if (!(total > 0)) return null
  // Lines without a weight can't be placed by share; keep them with the main ones.
  const isMinor = (l: StatementLine) => l.weight != null && l.weight / total <= MINOR_SHARE
  const major = sorted.filter(l => !isMinor(l))
  const minor = sorted.filter(isMinor)
  if (minor.length === 0) return null

  const majorKeys = new Set(major.map(l => itemKey(l.name)))
  // Split-out items in weight order, each with the most it could weigh.
  const items = new Map<string, { text: string; share: number }>()
  let flavor: { natural: boolean; artificial: boolean; share: number } | null = null
  for (const line of minor) {
    const share = (line.weight ?? 0) / total
    const sub = (line.sub_ingredients ?? '').trim()
    const parts = sub ? splitTopLevel(sub) : [line.name]
    for (const part of parts) {
      const text = cleanItem(part)
      if (!text) continue
      const f = flavorKind(text)
      if (f) {
        flavor ??= { natural: false, artificial: false, share: 0 }
        flavor.natural ||= f.natural
        flavor.artificial ||= f.artificial
        flavor.share += share
        if (!items.has(FLAVOR_KEY)) items.set(FLAVOR_KEY, { text: '', share: 0 })
        continue
      }
      const key = itemKey(text)
      if (majorKeys.has(key)) continue
      const prev = items.get(key)
      if (prev) prev.share += share
      else items.set(key, { text, share })
    }
  }
  if (flavor) {
    items.set(FLAVOR_KEY, { text: flavorTerm(flavor), share: flavor.share })
  }
  for (const it of items.values()) if (it.share > MINOR_SHARE + 1e-9) return null

  const head = major.map(fullEntry)
  const tail = [...items.values()].map(it => it.text)
  return `${head.length ? head.join(', ') + ', ' : ''}${MINOR_HEADING} ${tail.join(', ')}`
}

const FLAVOR_KEY = '\u0000flavor'

/** Splits "A, B (C, D), E" on commas outside parentheses. */
function splitTopLevel(text: string): string[] {
  const out: string[] = []
  let depth = 0
  let cur = ''
  for (const ch of text) {
    if (ch === '(' || ch === '[') depth++
    else if ((ch === ')' || ch === ']') && depth > 0) depth--
    if ((ch === ',' || ch === ';') && depth === 0) {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out.map(s => s.trim()).filter(Boolean)
}

/** Upper case, single spaces, FD&C names, no grade marks (USP, FCC, NF). */
function cleanItem(text: string): string {
  return normalizeColorNames(text)
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .replace(/\s+\b(?:USP|FCC|NF)\b\.?(?=\s*(?:\(|$))/g, '')
    .replace(/\.$/, '')
    .trim()
}

function itemKey(text: string): string {
  return cleanItem(text).replace(/\s*&\s*/g, ' AND ').replace(/[^A-Z0-9()]+/g, ' ').trim()
}

/** Generic flavor terms only; named flavors ("SMOKE FLAVOR", "ORANGE OIL") stay as typed. */
function flavorKind(text: string): { natural: boolean; artificial: boolean } | null {
  const m = itemKey(text).match(/^(NATURAL|ARTIFICIAL|NATURAL AND ARTIFICIAL|ARTIFICIAL AND NATURAL) FLAVOU?RS?$/)
  const kind = m?.[1]
  if (!kind) return null
  return { natural: kind.includes('NATURAL'), artificial: kind.includes('ARTIFICIAL') }
}

function flavorTerm(f: { natural: boolean; artificial: boolean }): string {
  if (f.natural && f.artificial) return 'NATURAL AND ARTIFICIAL FLAVORS'
  return f.natural ? 'NATURAL FLAVORS' : 'ARTIFICIAL FLAVORS'
}
